import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RefreshCw, BarChart3 } from 'lucide-react';
import {
  OverviewCards,
  LeadsTrendChart,
  CampaignTable,
  ConversionsTable,
  SourceBreakdown,
  DeviceBreakdown,
  QuoteAnalyticsSection
} from '../analytics';
import { UtmAnalyticsResponse, GroupedLeadConversion, QuoteDetail, AnalyticsActiveTab, UtmSubTab } from '../../types';
import { DateRange } from '../../hooks/useUtmAnalytics';

interface AnalyticsPageProps {
  analyticsSection: 'utm' | 'chapas' | 'medalhas';
  analytics: UtmAnalyticsResponse | null;
  conversions: GroupedLeadConversion[];
  loading: boolean;
  conversionsLoading: boolean;
  activeTab: AnalyticsActiveTab;
  setActiveTab: (tab: AnalyticsActiveTab) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  onRefresh: () => void;
  onExportCampaigns: () => void;
  onExportConversions: () => void;
  // Quotes modal props
  selectedEmail: string | null;
  emailQuotes: QuoteDetail[];
  quotesLoading: boolean;
  onRowClick: (email: string) => void;
  onCloseQuotes: () => void;
  // Access token for quote analytics tabs
  accessToken: string;
}

export function AnalyticsPage({
  analyticsSection,
  analytics,
  conversions,
  loading,
  conversionsLoading,
  activeTab,
  setActiveTab,
  dateRange,
  setDateRange,
  onRefresh,
  onExportCampaigns,
  onExportConversions,
  selectedEmail,
  emailQuotes,
  quotesLoading,
  onRowClick,
  onCloseQuotes,
  accessToken
}: AnalyticsPageProps) {
  // UTM sub-tab state (only used when section is 'utm')
  const [utmSubTab, setUtmSubTab] = useState<UtmSubTab>('overview');

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange({
      ...dateRange,
      [field]: value
    });
  };

  // Section titles for header
  const sectionTitles = {
    utm: 'Análise UTM',
    chapas: 'Análise de Orçamentos - Chapas',
    medalhas: 'Análise de Orçamentos - Medalhas',
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Relatórios</CardTitle>
              <CardDescription>
                {sectionTitles[analyticsSection]}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* UTM Section */}
      {analyticsSection === 'utm' && (
        <div className="space-y-6">
          {/* UTM Date Picker + Refresh */}
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex flex-wrap items-end gap-4">
                {/* Date Range Picker */}
                <div className="flex gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="startDate" className="text-xs">De</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => handleDateChange('startDate', e.target.value)}
                      className="w-[160px]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="endDate" className="text-xs">Até</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => handleDateChange('endDate', e.target.value)}
                      className="w-[160px]"
                    />
                  </div>
                </div>

                <Button variant="outline" onClick={onRefresh} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Overview Cards */}
          <OverviewCards analytics={analytics} loading={loading} />

          {/* UTM Sub-tabs */}
          <Tabs value={utmSubTab} onValueChange={(v) => setUtmSubTab(v as UtmSubTab)}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="campaigns">Campanhas</TabsTrigger>
              <TabsTrigger value="conversions">Conversões</TabsTrigger>
              <TabsTrigger value="sources">Fontes</TabsTrigger>
            </TabsList>

            {/* Overview Sub-tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <LeadsTrendChart
                  data={analytics?.timeSeries || []}
                  loading={loading}
                />
                <DeviceBreakdown
                  data={analytics?.deviceBreakdown || null}
                  loading={loading}
                />
              </div>

              {/* Quick Campaign Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Campanhas</CardTitle>
                  <CardDescription>As 5 campanhas com mais leads</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-[200px] flex items-center justify-center">
                      <div className="animate-pulse text-muted-foreground">Carregando...</div>
                    </div>
                  ) : (analytics?.campaignMetrics?.length || 0) === 0 ? (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      Nenhuma campanha encontrada
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {analytics?.campaignMetrics.slice(0, 5).map((campaign, index) => {
                        const maxLeads = analytics.campaignMetrics[0]?.leads || 1;
                        const percentage = (campaign.leads / maxLeads) * 100;

                        return (
                          <div key={`${campaign.campaign}-${index}`} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium truncate max-w-[200px]" title={campaign.campaign}>
                                {campaign.campaign}
                              </span>
                              <span className="text-muted-foreground">
                                {campaign.leads} leads ({campaign.conversionRate.toFixed(1)}% conv.)
                              </span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Campaigns Sub-tab */}
            <TabsContent value="campaigns">
              <CampaignTable
                data={analytics?.campaignMetrics || []}
                loading={loading}
                onExport={onExportCampaigns}
              />
            </TabsContent>

            {/* Conversions Sub-tab */}
            <TabsContent value="conversions">
              <ConversionsTable
                data={conversions}
                loading={conversionsLoading}
                onExport={onExportConversions}
                selectedEmail={selectedEmail}
                emailQuotes={emailQuotes}
                quotesLoading={quotesLoading}
                onRowClick={onRowClick}
                onCloseQuotes={onCloseQuotes}
              />
            </TabsContent>

            {/* Sources Sub-tab */}
            <TabsContent value="sources">
              <SourceBreakdown
                data={analytics?.sourceMetrics || []}
                loading={loading}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Chapas Section */}
      {analyticsSection === 'chapas' && (
        <QuoteAnalyticsSection domain="chapas" accessToken={accessToken} />
      )}

      {/* Medalhas Section */}
      {analyticsSection === 'medalhas' && (
        <QuoteAnalyticsSection domain="medalhas" accessToken={accessToken} />
      )}
    </div>
  );
}
